import React, { useCallback, useEffect, useRef } from 'react';
import useResize from '@/hooks/useResize';
import { cn } from '@/utils/styles';
import { Card } from '../shadcn/card';

interface HeaderContainerProps {
  children: React.ReactNode;
  isNavOpen?: boolean;
  closeNav?: () => void;
  className?: string;
}

const HeaderContainer = ({
  children,
  isNavOpen,
  closeNav,
  className,
}: HeaderContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useResize();

  const resizeContainer = useCallback(
    (transitionDuration: string) => {
      if (!containerRef?.current) {
        return;
      }
      const container = containerRef.current;
      container.style.transitionDuration = transitionDuration;

      const navBar = container.firstElementChild;

      if (!navBar) {
        return;
      }

      if (isNavOpen) {
        container.style.height = 'calc(100dvh - 42px)';
      } else {
        container.style.height = `${navBar.scrollHeight}px`;
      }
    },
    [isNavOpen, containerRef],
  );

  useEffect(() => {
    resizeContainer('0.1s');
  }, [isNavOpen]);

  useEffect(() => {
    resizeContainer('0s');
  }, [width]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current || !closeNav) {
        return;
      }
      if (!containerRef.current.contains(e.target as Node)) {
        closeNav();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <header
      className={cn(
        'z-[55] h-full w-full items-center !rounded-lg shadow-2xl',
        className,
      )}>
      <div
        className="z-[55] flex w-full flex-col gap-4 transition-all duration-200"
        ref={containerRef}>
        {children}
      </div>
    </header>
  );
};

export default HeaderContainer;
